import { useEffect, useState } from "react";
import { t } from "@/i18n-js/instance";
import { Icon } from "@/react/components/shared/Icon";
import { VIEWS } from "@/react/components/shared/MembersList/constants";
import { Button } from "@/react/components/shared/uikit/Button";
import { Loader } from "@/react/components/shared/uikit/Loader";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";
import {
  useCommunitySettingShow,
  useUpdateCommunitySetting,
} from "@/react/hooks/UseCommunitySetting";
import { useControlsDropdownContext } from "./context";

const i18nBase = "members_directory.controls_dropdown.view_selector";

const activeClassName = "!bg-secondary !border-secondary";

type View = "list" | "cards";

export const ViewSelector = () => {
  const { data, isLoading } = useCommunitySettingShow();

  const { setCurrentComponent } = useControlsDropdownContext();
  const updateCommunitySettingMutation = useUpdateCommunitySetting();

  const communitySetting = data?.community_settings;

  const [view, setView] = useState<View>(VIEWS.LIST);

  useEffect(() => {
    const currentView = communitySetting?.member_directory_settings?.view;
    if (currentView) {
      setView(currentView as View);
    }
  }, [communitySetting]);

  const handleSave = () => {
    updateCommunitySettingMutation.mutate(
      {
        data,
        updates: {
          member_directory_settings: {
            ...communitySetting?.member_directory_settings,
            view,
          },
        },
      },
      {
        onSuccess: () => {
          setCurrentComponent("root");
        },
      },
    );
  };

  const handleCancel = () => {
    setView(communitySetting?.member_directory_settings?.view as View);
    setCurrentComponent("root");
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      <Typography.LabelMd weight="semibold">
        {t(`${i18nBase}.title`)}
      </Typography.LabelMd>
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div className="mt-2 flex justify-between">
          <Button
            variant="secondary-light"
            className={classNames(
              "flex items-center gap-2 rounded-xl !px-7 !py-2.5",
              {
                [activeClassName]: view === VIEWS.LIST,
              },
            )}
            onClick={() => setView(VIEWS.LIST)}
          >
            <Icon type="20-list" size={20} />
            <Typography.LabelSm weight="medium" color="text-darkest">
              {t(`${i18nBase}.${VIEWS.LIST}`)}
            </Typography.LabelSm>
          </Button>

          <Button
            variant="secondary-light"
            className={classNames(
              "flex items-center gap-2 rounded-xl !px-7 !py-2.5",
              {
                [activeClassName]: view === VIEWS.CARDS,
              },
            )}
            onClick={() => setView(VIEWS.CARDS)}
          >
            <Icon type="20-cards" size={20} />
            <Typography.LabelSm weight="medium" color="text-darkest">
              {t(`${i18nBase}.${VIEWS.CARDS}`)}
            </Typography.LabelSm>
          </Button>
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="secondary" onClick={handleCancel}>
          {t(`${i18nBase}.cancel`)}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={updateCommunitySettingMutation.isLoading}
        >
          {t(`${i18nBase}.save`)}
        </Button>
      </div>
    </div>
  );
};
